/**
 * Domande sui ruoli globali dell'utente, in un posto solo.
 *
 * Il ruolo serve all'interfaccia per **non proporre** ciò che il backend
 * rifiuterebbe (le voci «Set di agenti», «Utenti» e «Ruoli» del menu, la
 * cancellazione di un progetto), non per decidere: la decisione resta del
 * backend, che risponde 403 comunque. Prima ogni pagina ripeteva
 * `user?.roles.includes('ADMIN') === true`; una funzione sola evita che due
 * pagine rispondano in modo diverso alla stessa domanda.
 */

import { useAuth, type AuthenticatedUser } from './AuthContext';

/**
 * Indica se l'utente è amministratore della piattaforma.
 *
 * @param user Utente autenticato, `null` se non c'è sessione.
 * @returns True se ha il ruolo `ADMIN`.
 */
export function isPlatformAdmin(user: AuthenticatedUser | null): boolean {
  return user?.roles.includes('ADMIN') === true;
}

/**
 * Indica se l'utente può creare progetti fuori da un set.
 *
 * @param user Utente autenticato, `null` se non c'è sessione.
 * @returns True se ha il ruolo `ADMIN` o `PROJECT_ADMIN`.
 */
export function canCreateProjects(user: AuthenticatedUser | null): boolean {
  return isPlatformAdmin(user) || user?.roles.includes('PROJECT_ADMIN') === true;
}

/**
 * Hook: l'utente corrente è amministratore della piattaforma?
 *
 * @returns True se ha il ruolo `ADMIN`.
 */
export function useIsPlatformAdmin(): boolean {
  return isPlatformAdmin(useAuth().user);
}
